/**
 * StaffDetailPage — Individual employee dashboard (routed via /personeel/:id)
 * Tabs: Overzicht | Gegevens | Contracten
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Spinner } from '../components/ui';
import { StaffDetailView } from '../components/personeel/StaffDetailView';
import { StaffGegevensView } from '../components/personeel/StaffGegevensView';
import { StaffForm } from '../components/personeel/StaffForm';
import { ContractList } from '../components/personeel/ContractList';
import { ContractForm, type ContractVerlenging } from '../components/personeel/ContractForm';
import { ContractBeslisKaarten } from '../components/personeel/ContractBeslisKaart';
import { ContractPreview } from '../components/personeel/ContractPreview';
import { UrensaldoMutationsView } from '../components/uren/UrensaldoMutationsView';
import { useWorkforceStore, useAuthStore } from '../stores';
import { supabase } from '../lib/supabase';
import type { Staff, Contract, ContractCreateData, ContractBeslispunt } from '../stores/workforce-store';

type Tab = 'overzicht' | 'gegevens' | 'contracten' | 'potje';
type GegevensView = 'read' | 'edit';
type ContractView = 'list' | 'form' | 'edit' | 'preview' | 'verleng';

// Velden die "Verlengen, nieuwe voorwaarden" naar contract-einde stuurt (de API filtert ook)
const VERLENG_OVERRIDES = [
  'contract_type', 'employment_type', 'functie', 'end_date', 'uren_per_week', 'uren_min', 'uren_max',
  'jaaruren_norm', 'cao_dienstjaren', 'loongroep', 'bruto_uurloon', 'bruto_maandsalaris',
  'vakantie_percentage', 'vakantietoeslag_percentage', 'vakantie_opbouw_per_week', 'vakantieuren_per_jaar',
  'bedrijf_vertegenwoordigd_door', 'notes', 'uurloon_via_cao',
] as const;

export function StaffDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    staffList, isLoadingStaff, fetchStaff, updateStaff, deleteStaff, createContract, updateContract, fetchContracts,
    signContractAsEmployer, contracts, beslispunten, fetchBeslispunten, besluitContractEinde,
  } = useWorkforceStore();
  const { user } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>(searchParams.get('tab') === 'contracten' ? 'contracten' : 'overzicht');
  const [verlenging, setVerlenging] = useState<{ beslispunt: ContractBeslispunt; form: ContractVerlenging } | null>(null);
  const [gegevensView, setGegevensView] = useState<GegevensView>('read');
  const [contractView, setContractView] = useState<ContractView>('list');
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [contractWarning, setContractWarning] = useState<string | null>(null);
  const [showUitDienst, setShowUitDienst] = useState(false);
  const [endDate, setEndDate] = useState('');
  const [statusSaving, setStatusSaving] = useState(false);
  const [reinviteBusy, setReinviteBusy] = useState(false);
  const [reinviteResult, setReinviteResult] = useState<{ ok: boolean; message: string } | null>(null);

  useEffect(() => {
    // incl. inactief/uitgenodigd, zodat ook een net uitgenodigde medewerker
    // (nog niet actief) via directe link/refresh gevonden wordt
    if (staffList.length === 0) fetchStaff(true);
  }, [staffList.length, fetchStaff]);

  const staff = staffList.find(s => s.id === id);

  // Open beslispunt van deze medewerker (contracteinde) bij de Contracten-tab
  useEffect(() => {
    if (id && activeTab === 'contracten') void fetchBeslispunten(id);
  }, [id, activeTab, fetchBeslispunten]);
  const mijnBeslispunten = beslispunten.filter(b => b.staff.id === id);

  // Deep links uit Telegram en de beslis-kaart: ?contract=<id> opent het contract
  // (werkgever tekenen), ?verleng=<beslispunt> opent het formulier nieuwe voorwaarden.
  const contractParam = searchParams.get('contract');
  const verlengParam = searchParams.get('verleng');
  useEffect(() => {
    if (!contractParam && !verlengParam) return;
    if (contractParam) {
      const c = contracts.find(x => x.id === contractParam);
      if (!c) return;
      setSelectedContract(c);
      setContractView('preview');
    } else if (verlengParam) {
      const b = beslispunten.find(x => x.id === verlengParam);
      const basis = b && contracts.find(x => x.id === b.contract.id);
      if (!b || !basis) return;
      startVerlenging(b, basis);
    }
    setActiveTab('contracten');
    setSearchParams(prev => { prev.delete('contract'); prev.delete('verleng'); return prev; }, { replace: true });
    // startVerlenging is stabiel genoeg: alleen state-setters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractParam, verlengParam, contracts, beslispunten, setSearchParams]);
  // Mark as seen when viewing a freshly-onboarded employee
  useEffect(() => {
    if (staff && staff.onboarding_status === 'completed' && !staff.onboarding_seen_at) {
      void updateStaff(staff.id, { onboarding_seen_at: new Date().toISOString() } as Partial<Staff>);
    }
  }, [staff, updateStaff]);

  if (isLoadingStaff && staffList.length === 0) {
    return (
      <>
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      </>
    );
  }

  if (!staff) {
    return (
      <>
        <div className="p-6 max-w-5xl overflow-y-auto h-full">
          <p className="text-muted">Medewerker niet gevonden</p>
          <button onClick={() => navigate('/personeel')} className="mt-4 text-indigo-600 hover:underline text-sm">
            Terug naar overzicht
          </button>
        </div>
      </>
    );
  }

  const handleSave = async (data: Partial<Staff>) => {
    setSaveError(null);
    const result = await updateStaff(staff.id, data);
    if (result) {
      setGegevensView('read');
    } else {
      setSaveError('Opslaan mislukt. Controleer of alle databasekolommen bestaan.');
    }
  };

  const handleDelete = async (staffId: string) => {
    if (confirm('Weet je zeker dat je deze medewerker wilt deactiveren?')) {
      await deleteStaff(staffId);
      navigate('/personeel');
    }
  };

  const openUitDienst = () => {
    setEndDate(staff.end_date || format(new Date(), 'yyyy-MM-dd'));
    setShowUitDienst(true);
  };

  const handleUitDienst = async () => {
    setStatusSaving(true);
    await updateStaff(staff.id, { is_active: false, end_date: endDate } as Partial<Staff>);
    setStatusSaving(false);
    setShowUitDienst(false);
  };

  const handleWeerInDienst = async () => {
    setStatusSaving(true);
    await updateStaff(staff.id, { is_active: true, end_date: null } as Partial<Staff>);
    setStatusSaving(false);
  };

  const handleReinvite = async () => {
    if (!staff.email) return;
    const isOpenInvite = staff.onboarding_status === 'invited';
    const msg = isOpenInvite
      ? `Er staat al een uitnodiging open. Nieuwe onboarding-link sturen naar ${staff.email}? De oude link vervalt.`
      : `Onboarding-link sturen naar ${staff.email}? ${staff.name} kan dan zelf zijn gegevens (opnieuw) invullen via het portaal.`;
    if (!confirm(msg)) return;

    setReinviteBusy(true);
    setReinviteResult(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch('/api/workforce/reinvite-staff', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ staff_id: staff.id }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Uitnodigen mislukt');
      setReinviteResult({ ok: true, message: `Uitnodiging verstuurd naar ${data.email}. De link is 14 dagen geldig.` });
      await fetchStaff(true);
    } catch (err) {
      setReinviteResult({ ok: false, message: err instanceof Error ? err.message : 'Onbekende fout' });
    } finally {
      setReinviteBusy(false);
    }
  };

  const syncLoonTypeFromContract = async (employmentType: string) => {
    if (!staff) return;
    const newLoonType = employmentType === '0-uren-allin' ? 'all-in' : 'bruto';
    if (newLoonType !== staff.loon_type) {
      await updateStaff(staff.id, { loon_type: newLoonType as 'bruto' | 'all-in' });
    }
  };

  const handleCreateContract = async (data: ContractCreateData) => {
    setContractWarning(null);
    const result = await createContract(data);
    if (result) {
      if (result.warning) setContractWarning(result.warning);
      await syncLoonTypeFromContract(data.employment_type);
      await fetchStaff(true); // loonkaart (uurloon/loongroep) is door de API bijgewerkt
      setContractView('list');
    } else {
      // o.a. 409 KETEN_ONBEPAALD: de API weigert een bepaald-contract dat van rechtswege onbepaald is
      setContractWarning(useWorkforceStore.getState().lastMutationError?.message ?? 'Contract aanmaken mislukt');
    }
  };

  function startVerlenging(b: ContractBeslispunt, basis: Contract) {
    if (!b.voorstel) return;
    setVerlenging({
      beslispunt: b,
      form: { basis, voorstel: b.voorstel, onbepaaldVerplicht: !!b.keten?.volgendeMoetOnbepaald, ketenReden: b.keten?.reden ?? null },
    });
    setContractView('verleng');
  }

  const handleVerlengNieuw = (b: ContractBeslispunt) => {
    const basis = contracts.find(c => c.id === b.contract.id);
    if (basis) startVerlenging(b, basis);
  };

  const handleVerlengSave = async (data: ContractCreateData) => {
    if (!verlenging) return;
    setContractWarning(null);
    const overrides: Record<string, unknown> = {};
    for (const veld of VERLENG_OVERRIDES) overrides[veld] = (data as Record<string, unknown>)[veld];
    const r = await besluitContractEinde(verlenging.beslispunt.id, 'verlengen_nieuw', overrides);
    if (!r.ok) {
      setContractWarning(r.error ?? 'Verlengen mislukt');
      return;
    }
    setContractWarning(r.mailOnderdrukt
      ? 'Verlengd. De aanzegmail is in deze stand (schaduw) niet verstuurd.'
      : 'Verlengd en aangezegd: de medewerker heeft de mail met de nieuwe voorwaarden, HR staat in cc.');
    setVerlenging(null);
    if (r.nieuwContract) {
      setSelectedContract(r.nieuwContract);
      setContractView('preview');
    } else {
      setContractView('list');
    }
  };

  const handleEditContract = async (data: ContractCreateData) => {
    if (!selectedContract) return;
    const result = await updateContract(selectedContract.id, data);
    if (result) {
      setSelectedContract(result);
      await fetchContracts(staff.id);
      await syncLoonTypeFromContract(data.employment_type);
      setContractView('preview');
    }
  };

  const handleEmployerSign = async (contractWithSig: Contract) => {
    if (!contractWithSig.employer_signature_data) return;
    const result = await signContractAsEmployer(
      contractWithSig.id,
      contractWithSig.employer_signature_data,
      user?.displayName || 'A.J. Blok'
    );
    if (result) {
      setSelectedContract(result);
      await fetchContracts(staff.id);
    }
  };

  const tabClass = (tab: Tab) =>
    `px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
      activeTab === tab
        ? 'border-indigo-500 text-indigo-600'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    }`;

  return (
    <>
      <div className="p-6 max-w-4xl mx-auto overflow-y-auto h-full">
        {/* Back link + name + dienst-status */}
        <div className="mb-4">
          <button onClick={() => navigate('/personeel')} className="text-sm text-indigo-600 hover:underline">
            &larr; Terug naar overzicht
          </button>
          <div className="flex items-center justify-between gap-3 mt-1">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold text-main">{staff.name}</h1>
              {staff.is_active ? (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">In dienst</span>
              ) : (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">Uit dienst</span>
              )}
              {staff.onboarding_status === 'invited' && (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Uitnodiging open</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {staff.email && (
                <button
                  onClick={handleReinvite}
                  disabled={reinviteBusy}
                  className="px-3 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors whitespace-nowrap disabled:opacity-50"
                >
                  {reinviteBusy ? 'Versturen…' : 'Opnieuw uitnodigen'}
                </button>
              )}
              {staff.is_active ? (
                <button
                  onClick={openUitDienst}
                  className="px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors whitespace-nowrap"
                >
                  Uit dienst zetten
                </button>
              ) : (
                <button
                  onClick={handleWeerInDienst}
                  disabled={statusSaving}
                  className="px-3 py-2 text-sm font-medium text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors whitespace-nowrap disabled:opacity-50"
                >
                  {statusSaving ? 'Verwerken…' : 'Weer in dienst'}
                </button>
              )}
            </div>
          </div>
          {!staff.is_active && staff.end_date && (
            <p className="text-xs text-muted mt-1">
              Uit dienst sinds {format(new Date(staff.end_date + 'T00:00:00'), 'd MMMM yyyy', { locale: nl })}
            </p>
          )}
        </div>

        {/* Re-invite resultaat */}
        {reinviteResult && (
          <div className={`mb-4 p-3 rounded-xl flex items-center justify-between border ${
            reinviteResult.ok ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
          }`}>
            <p className={`text-sm ${reinviteResult.ok ? 'text-emerald-800' : 'text-red-700'}`}>
              {reinviteResult.message}
            </p>
            <button
              onClick={() => setReinviteResult(null)}
              className={`text-xs ${reinviteResult.ok ? 'text-emerald-600 hover:text-emerald-800' : 'text-red-600 hover:text-red-800'}`}
            >
              Sluiten
            </button>
          </div>
        )}

        {/* Uit dienst-bevestiging */}
        {showUitDienst && (
          <div className="mb-6 bg-white border border-red-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-main mb-1">{staff.name} uit dienst zetten</h3>
            <p className="text-xs text-muted mb-3 max-w-lg">
              De medewerker verdwijnt direct uit het rooster, de team-app en de planning. Je kunt dit later terugdraaien met &ldquo;Weer in dienst&rdquo;.
            </p>
            <div className="mb-3 max-w-xs">
              <label className="block text-xs font-medium text-muted mb-1">Laatste werkdag</label>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleUitDienst}
                disabled={statusSaving || !endDate}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
              >
                {statusSaving ? 'Verwerken…' : 'Uit dienst zetten'}
              </button>
              <button onClick={() => setShowUitDienst(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm">
                Annuleren
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-0 border-b border-gray-200 mb-6">
          <button className={tabClass('overzicht')} onClick={() => setActiveTab('overzicht')}>
            Overzicht
          </button>
          <button className={tabClass('gegevens')} onClick={() => { setActiveTab('gegevens'); setGegevensView('read'); }}>
            Gegevens
          </button>
          <button className={tabClass('contracten')} onClick={() => { setActiveTab('contracten'); setContractView('list'); }}>
            Contracten
          </button>
          {staff.contract_type !== 'zzp' && staff.contract_type !== 'dga' && (
            <button className={tabClass('potje')} onClick={() => setActiveTab('potje')}>
              Potje
            </button>
          )}
        </div>

        {/* Ketenregeling warning */}
        {contractWarning && activeTab === 'contracten' && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            {contractWarning}
            <button onClick={() => setContractWarning(null)} className="ml-2 text-amber-600 hover:underline">sluiten</button>
          </div>
        )}

        {/* ── Overzicht tab ── */}
        {activeTab === 'overzicht' && (
          <StaffDetailView
            staff={staff}
            onBack={() => navigate('/personeel')}
            onEdit={() => { setActiveTab('gegevens'); setGegevensView('edit'); }}
          />
        )}

        {/* ── Gegevens tab ── */}
        {activeTab === 'gegevens' && (
          gegevensView === 'read' ? (
            <StaffGegevensView
              staff={staff}
              onEdit={() => setGegevensView('edit')}
            />
          ) : (
            <>
              {saveError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {saveError}
                </div>
              )}
              <StaffForm
                staff={staff}
                onSave={handleSave}
                onCancel={() => setGegevensView('read')}
                onDelete={handleDelete}
              />
            </>
          )
        )}

        {/* ── Potje tab ── */}
        {activeTab === 'potje' && (
          <UrensaldoMutationsView staffId={staff.id} staffName={staff.name} canEdit={true} />
        )}

        {/* ── Contracten tab ── */}
        {activeTab === 'contracten' && (
          contractView === 'list' ? (
            <>
            <ContractBeslisKaarten beslispunten={mijnBeslispunten} onVerlengNieuw={handleVerlengNieuw} />
            <ContractList
              staffId={staff.id}
              onNewContract={() => setContractView('form')}
              onViewContract={(contract) => {
                setSelectedContract(contract);
                setContractView('preview');
              }}
            />
            </>
          ) : contractView === 'verleng' && verlenging ? (
            <ContractForm
              staff={staff}
              verlenging={verlenging.form}
              onSave={handleVerlengSave}
              onCancel={() => { setVerlenging(null); setContractView('list'); }}
            />
          ) : contractView === 'form' ? (
            <ContractForm
              staff={staff}
              onSave={handleCreateContract}
              onCancel={() => setContractView('list')}
            />
          ) : contractView === 'edit' && selectedContract ? (
            <ContractForm
              staff={staff}
              contract={selectedContract}
              onSave={handleEditContract}
              onCancel={() => setContractView('preview')}
            />
          ) : selectedContract ? (
            <ContractPreview
              contract={selectedContract}
              onBack={() => { setContractView('list'); setSelectedContract(null); }}
              onEdit={() => setContractView('edit')}
              onSent={(updated) => setSelectedContract(updated)}
              onEmployerSign={handleEmployerSign}
            />
          ) : null
        )}
      </div>
    </>
  );
}
